import React, { useState, useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { importsApi } from "../../../services";
import type { ImportAttributes } from "../../../types";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  Loading,
  ErrorState,
  IconsLarge,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { ImportsListView } from "./ImportsListView";

export const Import: React.FC = () => {
  const { company } = useCompany();
  const companyId = company?.id ?? 0;
  const [imports, setImports] = useState<ImportAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [importToDelete, setImportToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchImports = async () => {
    if (companyId === 0) {
      setImports([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await importsApi.getAll(companyId);
      if (response.success && response.data) {
        setImports(response.data);
      } else {
        setError(response.error ?? "Falha ao carregar importações");
      }
    } catch {
      setError("Falha ao carregar importações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImports();
  }, [companyId]);

  const handleDelete = (id: number) => {
    setImportToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!importToDelete) return;
    setDeleteLoading(true);
    setError(null);
    try {
      const response = await importsApi.deleteTransactions(importToDelete);
      if (response.success) {
        await fetchImports();
        setShowDeleteModal(false);
        setImportToDelete(null);
      } else {
        setError(response.error ?? "Falha ao excluir importação");
      }
    } catch {
      setError("Falha ao excluir importação");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setImportToDelete(null);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<IconsLarge.Import />}
        title="Importações"
        subtitle="Histórico de importações da empresa"
        showViewToggle={false}
      />

      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => fetchImports()}
        />
      )}

      {loading && !showDeleteModal ? (
        <Loading
          message="Carregando importações..."
          size="xl"
        />
      ) : (
        <Card className="p-8">
          {imports.length === 0 ? (
            <EmptyState
              icon={<IconsLarge.Import />}
              title="Nenhuma importação encontrada"
              description="As importações realizadas aparecerão aqui."
            />
          ) : (
            <ImportsListView
              imports={imports}
              onDelete={handleDelete}
              loading={loading}
            />
          )}
        </Card>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir importação"
        message="Tem certeza que deseja excluir esta importação? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Import;
